import React, { useState, useEffect, useRef } from 'react';
import { Play, Square, Volume2, VolumeX, Trash2 } from 'lucide-react';

function CPC3000() {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isPlayingSecret, setIsPlayingSecret] = useState(false);
  const [bpm, setBpm] = useState(88);
  const [currentStep, setCurrentStep] = useState(-1);
  const [masterVolume, setMasterVolume] = useState(0.7);
  const [pressedKey, setPressedKey] = useState(null);
  const [octave, setOctave] = useState(3);
  const [selectedInstrument, setSelectedInstrument] = useState('piano');
  const [activePreset, setActivePreset] = useState(null);
  const [selectedTrackId, setSelectedTrackId] = useState(null);
  const [eqLevels, setEqLevels] = useState(Array(12).fill(0));
  const [isRecordingMaster, setIsRecordingMaster] = useState(false);
  const [recordingType, setRecordingType] = useState(null);
  const [recordedTracks, setRecordedTracks] = useState([]);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [activeEffects, setActiveEffects] = useState({ reverb: false, distortion: false, delay: false });
  const [selectedStep, setSelectedStep] = useState(null);
  const [stepNotes, setStepNotes] = useState({});
  const [isRecordingSample, setIsRecordingSample] = useState(false);
  const [sampleBuffer, setSampleBuffer] = useState(null);
  const [micLevel, setMicLevel] = useState(0);
  const [drumInstruments, setDrumInstruments] = useState({ tom: 'tom', crash: 'crash', ride: 'ride' });
  const [recordingKeyMap, setRecordingKeyMap] = useState({});
  const [editingRecordingId, setEditingRecordingId] = useState(null);
  const [editingRecordingName, setEditingRecordingName] = useState('');
  
  const [effects, setEffects] = useState({
    nitroLow: 50, nitroHigh: 50, nitroRes: 0, punchCutoff: 50, punchRes: 0,
    gridDelay: 30, gridFeedback: 20, drive: 0
  });
  
  const [drumTrack, setDrumTrack] = useState({
    kick: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], snare: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
    hat: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0],
    crash: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0]
  });
  
  const [trackVolumes, setTrackVolumes] = useState({
    piano: 0.8, kick: 1.0, snare: 0.9, hat: 0.6, tom: 0.8, crash: 0.7, ride: 0.7,
    clap: 1.0, cowbell: 0.8, shaker: 0.6
  });
  
  const [trackMutes, setTrackMutes] = useState({
    piano: false, kick: false, snare: false, hat: false, tom: false, crash: false, ride: false,
    clap: false, cowbell: false, shaker: false
  });

  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const reverbNodeRef = useRef(null);
  const distortionNodeRef = useRef(null);
  const delayNodeRef = useRef(null);
  const nextNoteTimeRef = useRef(0);
  const timerIdRef = useRef(null);
  const currentStepIndexRef = useRef(0);
  const secretTimeoutsRef = useRef([]);
  const activeOscillatorsRef = useRef([]);
  const mediaRecorderRef = useRef(null);
  const mediaStreamRef = useRef(null);
  const micAnalyserRef = useRef(null);
  const metronomeStepRef = useRef(0);
  const levelIntervalRef = useRef(null);

  const metronomeNeedlePosition = React.useMemo(() => {
    return 50 + Math.sin(((metronomeStepRef.current % 16) / 16) * Math.PI * 2 - Math.PI / 2) * 45;
  }, [currentStep]);

  useEffect(() => {
    audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
    analyserRef.current = audioContextRef.current.createAnalyser();
    analyserRef.current.fftSize = 256;
    analyserRef.current.connect(audioContextRef.current.destination);
    
    const ctx = audioContextRef.current;
    const convolver = ctx.createConvolver();
    const reverbLength = ctx.sampleRate * 2;
    const reverbBuffer = ctx.createBuffer(2, reverbLength, ctx.sampleRate);
    for (let channel = 0; channel < 2; channel++) {
      const channelData = reverbBuffer.getChannelData(channel);
      for (let i = 0; i < reverbLength; i++) {
        channelData[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / reverbLength, 2);
      }
    }
    convolver.buffer = reverbBuffer;
    reverbNodeRef.current = convolver;
    
    const distortion = ctx.createWaveShaper();
    const samples = 256;
    const curve = new Float32Array(samples);
    for (let i = 0; i < samples; i++) {
      const x = (i * 2) / samples - 1;
      curve[i] = (3 + 100) * x / (Math.PI + 100 * Math.abs(x));
    }
    distortion.curve = curve;
    distortionNodeRef.current = distortion;
    
    const delay = ctx.createDelay(1.0);
    delay.delayTime.value = 0.3;
    const delayFeedback = ctx.createGain();
    delayFeedback.gain.value = 0.4;
    delay.connect(delayFeedback);
    delayFeedback.connect(delay);
    delayNodeRef.current = { delay, feedback: delayFeedback };
    
    return () => {
      if (audioContextRef.current?.state !== 'closed') audioContextRef.current?.close();
      if (mediaStreamRef.current) {
        if (mediaStreamRef.current.getTracks) mediaStreamRef.current.getTracks().forEach(t => t.stop());
        mediaStreamRef.current = null;
      }
      if (micAnalyserRef.current?.source) micAnalyserRef.current.source.disconnect();
      if (levelIntervalRef.current) clearInterval(levelIntervalRef.current);
    };
  }, []);

  useEffect(() => {
    if (isRecordingMaster) {
      const timeTimer = setInterval(() => setElapsedTime(p => p + 0.1), 100);
      return () => clearInterval(timeTimer);
    } else {
      setElapsedTime(0);
    }
  }, [isRecordingMaster]);

  useEffect(() => {
    if (isPlaying || isPlayingSecret) {
      const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount);
      const eqTimer = setInterval(() => {
        analyserRef.current.getByteFrequencyData(dataArray);
        const newLevels = [];
        for (let i = 0; i < 12; i++) {
          const start = i * Math.floor(dataArray.length / 12);
          const end = start + Math.floor(dataArray.length / 12);
          let sum = 0;
          for (let j = start; j < end; j++) sum += dataArray[j];
          newLevels.push(Math.min(100, Math.max(5, (sum / (end - start) / 255) * 100)));
        }
        setEqLevels(newLevels);
      }, 50);
      return () => clearInterval(eqTimer);
    } else {
      setEqLevels(Array(12).fill(0));
    }
  }, [isPlaying, isPlayingSecret]);

  const connectToOutput = (gainNode) => {
    const ctx = audioContextRef.current;
    const analyser = analyserRef.current;
    const dryGain = ctx.createGain();
    const wetGain = ctx.createGain();
    gainNode.connect(dryGain);
    gainNode.connect(wetGain);
    let currentNode = wetGain;
    
    if (activeEffects.distortion && distortionNodeRef.current) {
      currentNode.connect(distortionNodeRef.current);
      currentNode = distortionNodeRef.current;
    }
    if (activeEffects.delay && delayNodeRef.current) {
      currentNode.connect(delayNodeRef.current.delay);
      delayNodeRef.current.delay.connect(analyser);
    }
    if (activeEffects.reverb && reverbNodeRef.current) {
      const reverbGain = ctx.createGain();
      reverbGain.gain.value = 0.6;
      currentNode.connect(reverbNodeRef.current);
      reverbNodeRef.current.connect(reverbGain);
      reverbGain.connect(analyser);
    }
    
    dryGain.connect(analyser);
    if (activeEffects.reverb || activeEffects.distortion || activeEffects.delay) {
      dryGain.gain.value = 0.5;
      wetGain.gain.value = 0.7;
    } else {
      dryGain.gain.value = 1.0;
      wetGain.gain.value = 0;
    }
    
    if (!activeEffects.delay && !activeEffects.reverb && activeEffects.distortion) {
      currentNode.connect(analyser);
    }
  };

  const noteFreqs = {
    C: 261.63, Db: 277.18, D: 293.66, Eb: 311.13, E: 329.63, F: 349.23,
    Gb: 369.99, G: 392.00, Ab: 415.30, A: 440.00, Bb: 466.16, B: 493.88
  };

  const midiToFreq = (midi) => 440 * Math.pow(2, (midi - 69) / 12);

  const playDrum = (type, time, volume) => {
    const ctx = audioContextRef.current;
    const vol = volume * masterVolume;
    
    if (type === 'kick') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      connectToOutput(gain);
      osc.frequency.setValueAtTime(150, time);
      osc.frequency.exponentialRampToValueAtTime(40, time + 0.15);
      gain.gain.setValueAtTime(vol * 3, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.4);
      osc.start(time);
      osc.stop(time + 0.4);
    } else if (type === 'snare') {
      const bufferSize = ctx.sampleRate * 0.15;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 1000;
      noise.connect(filter);
      filter.connect(gain);
      gain.gain.setValueAtTime(vol * 2, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.2);
      connectToOutput(gain);
      noise.start(time);
      
      const tone = ctx.createOscillator();
      const toneGain = ctx.createGain();
      tone.connect(toneGain);
      connectToOutput(toneGain);
      tone.frequency.setValueAtTime(200, time);
      tone.frequency.exponentialRampToValueAtTime(100, time + 0.1);
      toneGain.gain.setValueAtTime(vol * 0.5, time);
      toneGain.gain.exponentialRampToValueAtTime(0.001, time + 0.15);
      tone.start(time);
      tone.stop(time + 0.15);
    } else if (type === 'hat') {
      const bufferSize = ctx.sampleRate * 0.05;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 7000;
      noise.connect(filter);
      filter.connect(gain);
      gain.gain.setValueAtTime(vol * 1, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
      connectToOutput(gain);
      noise.start(time);
    } else if (type === 'crash') {
      const bufferSize = ctx.sampleRate * 1.5;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 3000;
      noise.connect(filter);
      filter.connect(gain);
      gain.gain.setValueAtTime(vol * 1.5, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 1.5);
      connectToOutput(gain);
      noise.start(time);
    } else if (type === 'ride') {
      const bufferSize = ctx.sampleRate * 0.6;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'bandpass';
      filter.frequency.value = 5000;
      filter.Q.value = 2;
      noise.connect(filter);
      filter.connect(gain);
      gain.gain.setValueAtTime(vol * 0.9, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.6);
      connectToOutput(gain);
      noise.start(time);
      
      const bell = ctx.createOscillator();
      const bellGain = ctx.createGain();
      bell.connect(bellGain);
      connectToOutput(bellGain);
      bell.frequency.setValueAtTime(800, time);
      bell.type = 'sine';
      bellGain.gain.setValueAtTime(vol * 0.3, time);
      bellGain.gain.exponentialRampToValueAtTime(0.001, time + 0.6);
      bell.start(time);
      bell.stop(time + 0.6);
    } else if (type === 'tom') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      connectToOutput(gain);
      osc.frequency.setValueAtTime(180, time);
      osc.frequency.exponentialRampToValueAtTime(80, time + 0.08);
      gain.gain.setValueAtTime(vol * 2, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.3);
      osc.start(time);
      osc.stop(time + 0.3);
    } else if (type === 'clap') {
      for (let i = 0; i < 3; i++) {
        const bufferSize = ctx.sampleRate * 0.05;
        const noise = ctx.createBufferSource();
        const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
        const output = buffer.getChannelData(0);
        for (let j = 0; j < bufferSize; j++) output[j] = Math.random() * 2 - 1;
        noise.buffer = buffer;
        const gain = ctx.createGain();
        const filter = ctx.createBiquadFilter();
        filter.type = 'bandpass';
        filter.frequency.value = 1500;
        noise.connect(filter);
        filter.connect(gain);
        gain.gain.setValueAtTime(vol * 1.5, time + i * 0.02);
        gain.gain.exponentialRampToValueAtTime(0.001, time + i * 0.02 + 0.08);
        connectToOutput(gain);
        noise.start(time + i * 0.02);
      }
    } else if (type === 'cowbell') {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      osc1.connect(gain);
      osc2.connect(gain);
      connectToOutput(gain);
      osc1.frequency.setValueAtTime(540, time);
      osc2.frequency.setValueAtTime(800, time);
      osc1.type = 'square';
      osc2.type = 'square';
      gain.gain.setValueAtTime(vol * 0.8, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.5);
      osc1.start(time);
      osc2.start(time);
      osc1.stop(time + 0.5);
      osc2.stop(time + 0.5);
    } else if (type === 'shaker') {
      const bufferSize = ctx.sampleRate * 0.04;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      const filter = ctx.createBiquadFilter();
      filter.type = 'highpass';
      filter.frequency.value = 8000;
      noise.connect(filter);
      filter.connect(gain);
      gain.gain.setValueAtTime(vol * 0.8, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
      connectToOutput(gain);
      noise.start(time);
    } else {
      const bufferSize = ctx.sampleRate * 0.1;
      const noise = ctx.createBufferSource();
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate);
      const output = buffer.getChannelData(0);
      for (let i = 0; i < bufferSize; i++) output[i] = Math.random() * 2 - 1;
      noise.buffer = buffer;
      const gain = ctx.createGain();
      gain.gain.setValueAtTime(vol * 1.5, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.1);
      noise.connect(gain);
      connectToOutput(gain);
      noise.start(time);
    }
  };

  const playInstrument = (note, time, instrument, octaveShift = 0) => {
    const ctx = audioContextRef.current;
    const freq = noteFreqs[note] * Math.pow(2, (octave + octaveShift) - 4);
    const volume = trackVolumes.piano * masterVolume;
    
    if (instrument === 'piano') {
      for (let h = 1; h <= 8; h++) {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        connectToOutput(gain);
        osc.type = h <= 3 ? 'sine' : 'triangle';
        osc.frequency.setValueAtTime(freq * h, time);
        const amp = (0.5 / Math.pow(h, 0.9)) * volume;
        gain.gain.setValueAtTime(0, time);
        gain.gain.linearRampToValueAtTime(amp, time + 0.01);
        gain.gain.exponentialRampToValueAtTime(0.001, time + 2);
        osc.start(time);
        osc.stop(time + 2);
      }
    } else if (instrument === 'bass') {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      connectToOutput(gain);
      osc.type = 'sawtooth';
      osc.frequency.setValueAtTime(freq / 2, time);
      gain.gain.setValueAtTime(volume * 0.6, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.8);
      osc.start(time);
      osc.stop(time + 0.8);
    } else {
      const osc1 = ctx.createOscillator();
      const osc2 = ctx.createOscillator();
      const gain = ctx.createGain();
      osc1.connect(gain);
      osc2.connect(gain);
      connectToOutput(gain);
      osc1.type = 'sawtooth';
      osc2.type = 'square';
      osc1.frequency.setValueAtTime(freq, time);
      osc2.frequency.setValueAtTime(freq * 1.01, time);
      gain.gain.setValueAtTime(volume * 0.4, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 1.5);
      osc1.start(time);
      osc2.start(time);
      osc1.stop(time + 1.5);
      osc2.stop(time + 1.5);
    }
  };

  const playKey = (note, drumType, keyIndex = null) => {
    const time = audioContextRef.current.currentTime;
    
    if (selectedInstrument === 'recordings' && keyIndex !== null) {
      const mappedRecording = recordingKeyMap[keyIndex];
      console.log('Recordings mode - key index:', keyIndex, 'mapped ID:', mappedRecording);
      if (mappedRecording) {
        const recording = recordedTracks.find(t => t.id === mappedRecording);
        console.log('Found recording:', recording?.name, 'has data:', !!recording?.recording);
        if (recording?.recording) {
          if (selectedStep !== null) {
            const recordingId = 'rec-' + recording.id + '-key' + keyIndex;
            setStepNotes(prev => {
              const current = prev[selectedStep] || [];
              const isActive = current.some(n => n.id === recordingId);
              const newNotes = isActive ? current.filter(n => n.id !== recordingId) :
                [...current, { id: recordingId, note, index: keyIndex, recording }];
              if (newNotes.length === 0) {
                const updated = {...prev};
                delete updated[selectedStep];
                return updated;
              }
              return { ...prev, [selectedStep]: newNotes };
            });
            console.log('Added recording to step', selectedStep);
            setPressedKey(note);
            setTimeout(() => setPressedKey(null), 150);
            return;
          }
          console.log('Playing mapped recording immediately');
          playRecordedTrack(recording.recording);
          setPressedKey(note);
          return;
        }
      } else {
        console.log('No recording mapped to this key');
      }
    }
    
    if (selectedStep !== null && selectedInstrument !== 'drums' && selectedInstrument !== 'recordings' && keyIndex !== null) {
      const noteId = note + '-' + keyIndex;
      setStepNotes(prev => {
        const current = prev[selectedStep] || [];
        const isActive = current.some(n => n.id === noteId);
        const newNotes = isActive ? current.filter(n => n.id !== noteId) :
          [...current, { id: noteId, note, index: keyIndex }];
        if (newNotes.length === 0) {
          const updated = {...prev};
          delete updated[selectedStep];
          return updated;
        }
        console.log('Added note', note, 'to step', selectedStep);
        return { ...prev, [selectedStep]: newNotes };
      });
      setPressedKey(note);
      setTimeout(() => setPressedKey(null), 150);
      return;
    }
    
    if (selectedInstrument === 'drums' && drumType) {
      playDrum(drumType, time, trackVolumes[drumType] || 1.0);
      setPressedKey(note);
    } else {
      playInstrument(note, time, selectedInstrument);
      setPressedKey(note);
    }
  };

  const playRecordedTrack = (recording) => {
    if (!recording || recording.length === 0) {
      console.log('No recording data');
      return;
    }
    const startTime = audioContextRef.current.currentTime;
    console.log('Playing recording with', recording.length, 'events');
    recording.forEach(noteEvent => {
      const playTime = startTime + noteEvent.time;
      const delay = (playTime - audioContextRef.current.currentTime) * 1000;
      if (delay >= 0) {
        setTimeout(() => {
          if (noteEvent.buffer) {
            console.log('Playing buffer, duration:', noteEvent.buffer.duration);
            const source = audioContextRef.current.createBufferSource();
            const gain = audioContextRef.current.createGain();
            source.buffer = noteEvent.buffer;
            source.connect(gain);
            connectToOutput(gain);
            gain.gain.setValueAtTime(trackVolumes.piano * masterVolume * 1.2, audioContextRef.current.currentTime);
            source.start(audioContextRef.current.currentTime);
            source.stop(audioContextRef.current.currentTime + noteEvent.buffer.duration);
          } else if (noteEvent.note && noteFreqs[noteEvent.note]) {
            playInstrument(noteEvent.note, audioContextRef.current.currentTime, 
              noteEvent.instrument || 'piano', noteEvent.octave - octave);
          }
        }, Math.max(0, delay));
      }
    });
  };

  const startSampleRecording = async () => {
    try {
      if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume();
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          autoGainControl: false
        } 
      });
      mediaStreamRef.current = stream;
      const source = audioContextRef.current.createMediaStreamSource(stream);
      const analyser = audioContextRef.current.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;
      source.connect(analyser);
      micAnalyserRef.current = { analyser, source };
      
      levelIntervalRef.current = setInterval(() => {
        if (micAnalyserRef.current?.analyser) {
          const dataArray = new Uint8Array(analyser.frequencyBinCount);
          analyser.getByteFrequencyData(dataArray);
          let sum = 0;
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i];
          }
          const average = sum / dataArray.length;
          setMicLevel(Math.min(100, (average / 255) * 500));
        }
      }, 50);
      
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        if (levelIntervalRef.current) {
          clearInterval(levelIntervalRef.current);
          levelIntervalRef.current = null;
        }
        setMicLevel(0);
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        try {
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          setRecordedTracks(prev => {
            const newRecording = { 
              id: Date.now(), 
              name: 'Sample ' + (prev.filter(t => t.isSample).length + 1), 
              recording: [{ time: 0, buffer: audioBuffer }],
              duration: audioBuffer.duration,
              isSample: true
            };
            console.log('Saving mic recording:', newRecording);
            return [...prev, newRecording];
          });
        } catch (err) { 
          console.error('Audio decode error:', err);
          alert('Error processing audio.'); 
        }
        if (mediaStreamRef.current && mediaStreamRef.current.getTracks) {
          mediaStreamRef.current.getTracks().forEach(t => t.stop());
          mediaStreamRef.current = null;
        }
        if (micAnalyserRef.current?.source) {
          micAnalyserRef.current.source.disconnect();
          micAnalyserRef.current = null;
        }
      };
      recorder.start(100);
      setIsRecordingSample(true);
      setRecordingType('microphone');
    } catch (err) {
      console.error('Microphone error:', err);
      alert('Microphone access denied.');
      setIsRecordingSample(false);
      setRecordingType(null);
    }
  };

  const startMasterRecording = async () => {
    try {
      if (audioContextRef.current.state === 'suspended') await audioContextRef.current.resume();
      
      const dest = audioContextRef.current.createMediaStreamDestination();
      analyserRef.current.connect(dest);
      
      mediaStreamRef.current = dest.stream;
      
      const recorder = new MediaRecorder(dest.stream);
      mediaRecorderRef.current = recorder;
      const chunks = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        try {
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          
          setRecordedTracks(prev => {
            const newRecording = {
              id: Date.now(),
              name: 'Recording ' + (prev.filter(t => !t.isSample).length + 1),
              recording: [{ time: 0, buffer: audioBuffer }],
              duration: audioBuffer.duration,
              isSample: false
            };
            console.log('Saving master recording:', newRecording);
            return [...prev, newRecording];
          });
        } catch (err) {
          console.error('Audio decode error:', err);
          alert('Error processing recording.');
        }
        analyserRef.current.disconnect(dest);
        mediaStreamRef.current = null;
      };
      
      recorder.start(100);
      setIsRecordingMaster(true);
      setRecordingType('master');
    } catch (err) {
      console.error('Recording error:', err);
      alert('Error starting recording.');
    }
  };

  const stopSampleRecording = () => {
    if (mediaRecorderRef.current && isRecordingSample) {
      mediaRecorderRef.current.stop();
      setIsRecordingSample(false);
      setRecordingType(null);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins + ':' + secs.toString().padStart(2, '0');
  };

  const scheduleNote = (stepIndex, time) => {
    if (!trackMutes.kick && drumTrack.kick[stepIndex]) playDrum('kick', time, trackVolumes.kick);
    if (!trackMutes.snare && drumTrack.snare[stepIndex]) playDrum('snare', time, trackVolumes.snare);
    if (!trackMutes.hat && drumTrack.hat[stepIndex]) playDrum('hat', time, trackVolumes.hat);
    
    const tomInst = drumInstruments.tom;
    const crashInst = drumInstruments.crash;
    const rideInst = drumInstruments.ride;
    
    if (!trackMutes[tomInst] && drumTrack.tom[stepIndex]) playDrum(tomInst, time, trackVolumes[tomInst] || trackVolumes.tom);
    if (!trackMutes[crashInst] && drumTrack.crash[stepIndex]) playDrum(crashInst, time, trackVolumes[crashInst] || trackVolumes.crash);
    if (!trackMutes[rideInst] && drumTrack.ride[stepIndex]) playDrum(rideInst, time, trackVolumes[rideInst] || trackVolumes.ride);
    
    if (stepNotes[stepIndex]?.length > 0 && !trackMutes.piano) {
      stepNotes[stepIndex].forEach(noteObj => {
        if (noteObj.recording) {
          playRecordedTrack(noteObj.recording.recording);
        } else {
          const note = typeof noteObj === 'string' ? noteObj : noteObj.note;
          playInstrument(note, time, selectedInstrument === 'drums' ? 'piano' : selectedInstrument, 0);
        }
      });
    }
    
    if (metronomeEnabled && stepIndex % 4 === 0) {
      const osc = audioContextRef.current.createOscillator();
      const gain = audioContextRef.current.createGain();
      osc.connect(gain);
      gain.connect(audioContextRef.current.destination);
      osc.frequency.setValueAtTime(stepIndex === 0 ? 1200 : 800, time);
      gain.gain.setValueAtTime(0.3, time);
      gain.gain.exponentialRampToValueAtTime(0.001, time + 0.05);
      osc.start(time);
      osc.stop(time + 0.05);
    }
    metronomeStepRef.current = stepIndex;
  };

  const scheduler = () => {
    const ctx = audioContextRef.current;
    if (!ctx) return;
    const secondsPer16th = (60.0 / bpm) / 4;
    while (nextNoteTimeRef.current < ctx.currentTime + 0.1) {
      scheduleNote(currentStepIndexRef.current, nextNoteTimeRef.current);
      setCurrentStep(currentStepIndexRef.current);
      currentStepIndexRef.current = (currentStepIndexRef.current + 1) % 16;
      nextNoteTimeRef.current += secondsPer16th;
    }
    timerIdRef.current = setTimeout(scheduler, 75);
  };

  useEffect(() => {
    if (isPlaying && !isPlayingSecret) {
      currentStepIndexRef.current = 0;
      nextNoteTimeRef.current = audioContextRef.current.currentTime;
      scheduler();
    } else if (!isPlayingSecret) {
      if (timerIdRef.current) clearTimeout(timerIdRef.current);
      setCurrentStep(-1);
    }
    if (!isPlaying && isPlayingSecret) {
      secretTimeoutsRef.current.forEach(id => clearTimeout(id));
      secretTimeoutsRef.current = [];
      activeOscillatorsRef.current.forEach(osc => { try { osc.stop(); } catch(e) {} });
      activeOscillatorsRef.current = [];
      setIsPlayingSecret(false);
    }
    return () => { if (timerIdRef.current) clearTimeout(timerIdRef.current); };
  }, [isPlaying, isPlayingSecret, drumTrack, stepNotes, bpm, trackMutes, trackVolumes, masterVolume, drumInstruments]);

  const toggleDrumStep = (drum, step) => {
    setDrumTrack(prev => ({
      ...prev,
      [drum]: prev[drum].map((val, idx) => idx === step ? (val ? 0 : 1) : val)
    }));
  };

  const loadInspoTrack = (trackName) => {
    setActivePreset(trackName);
    const tracks = {
      thinking: { bpm: 75, drums: { kick: [1,0,0,0,1,0,0,0,1,0,0,0,1,0,0,0], snare: [0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0], hat: [1,0,1,0,1,0,1,0,1,0,1,0,1,0,1,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], crash: [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] }},
      creating: { bpm: 95, drums: { kick: [1,0,1,0,0,0,1,0,1,0,0,1,0,0,1,0], snare: [0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0], hat: [1,1,0,1,1,1,0,1,1,1,0,1,1,1,0,1], tom: [0,0,0,0,0,0,0,0,0,0,1,0,0,0,0,0], crash: [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] }},
      coding: { bpm: 90, drums: { kick: [1,0,0,0,0,0,1,0,1,0,0,0,0,0,1,0], snare: [0,0,0,0,1,0,0,0,0,0,0,0,1,0,0,0], hat: [1,0,1,0,1,0,1,0,1,0,1,0,1,0,1,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], crash: [1,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] }}
    };
    if (tracks[trackName]) {
      setBpm(tracks[trackName].bpm);
      setDrumTrack(tracks[trackName].drums);
      setStepNotes({});
    }
  };

  const drumPads = [
    {note: 'C', label: 'KICK', drum: 'kick'}, {note: 'Db', label: 'SNARE', drum: 'snare'},
    {note: 'D', label: 'HAT', drum: 'hat'}, {note: 'Eb', label: 'TOM', drum: 'tom'},
    {note: 'E', label: 'CLAP', drum: 'clap'}, {note: 'F', label: 'CRASH', drum: 'crash'},
    {note: 'Gb', label: 'RIDE', drum: 'ride'}, {note: 'G', label: 'COWBELL', drum: 'cowbell'},
    {note: 'Ab', label: 'SHAKER', drum: 'shaker'}, {note: 'A', label: 'TOM 2', drum: 'tom'},
    {note: 'Bb', label: 'CLAP 2', drum: 'clap'}, {note: 'B', label: 'PERC', drum: 'tom'},
    {note: 'C', label: 'PERC 2', drum: 'shaker'}, {note: 'D', label: 'PERC 3', drum: 'cowbell'}
  ];

  const whiteKeys = ['C', 'D', 'E', 'F', 'G', 'A', 'B', 'C', 'D', 'E', 'F', 'G', 'A', 'B'];
  const blackKeys = [{pos:0,note:'Db'},{pos:1,note:'Eb'},{pos:3,note:'Gb'},{pos:4,note:'Ab'},{pos:5,note:'Bb'},{pos:7,note:'Db'},{pos:8,note:'Eb'},{pos:10,note:'Gb'},{pos:11,note:'Ab'},{pos:12,note:'Bb'}];
  
  const allKeys = [...whiteKeys.map((n, i) => ({note: n, index: i, isBlack: false})), ...blackKeys.map((k, i) => ({note: k.note, index: 14 + i, isBlack: true, pos: k.pos}))];

  const VolumeSlider = ({ drum, isCustom = false }) => {
    const drumKey = isCustom ? drumInstruments[drum] : drum;
    const vol = trackVolumes[drumKey] || trackVolumes[drum] || 0.8;
    const muted = trackMutes[drumKey];
    
    return (
      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <div style={{ position: 'relative', width: 140, height: 24, display: 'flex', alignItems: 'center' }}>
          <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 3, background: '#0a0a0a', boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.9)', borderRadius: 2 }} />
          <div style={{ position: 'absolute', left: 0, top: '50%', transform: 'translateY(-50%)', width: (vol * 100) + '%', height: 3, background: 'linear-gradient(to right, #87b5d4, #a7d5f4)', borderRadius: 2, transition: 'width 0.1s' }} />
          <div style={{ position: 'absolute', left: (vol * 100) + '%', top: '50%', transform: 'translate(-50%, -50%)', width: 28, height: 20, background: 'linear-gradient(135deg, #d0d0d0, #b0b0b0, #a0a0a0)', border: '3px solid #707070', borderRadius: 3, boxShadow: '0 4px 8px rgba(0,0,0,0.8), inset 0 2px 0 rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }}>
            <div style={{ width: 2, height: 14, background: '#505050', borderRadius: 1 }} />
          </div>
          <input type="range" min="0" max="1" step="0.01" value={vol} onChange={(e) => setTrackVolumes(prev => ({ ...prev, [drumKey]: parseFloat(e.target.value) }))} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
        </div>
        <div style={{ background: '#1a1a1a', border: '2px solid #0a0a0a', borderRadius: 3, padding: '3px 6px', minWidth: 32, textAlign: 'center' }}>
          <div style={{ fontSize: 9, fontWeight: 'bold', color: '#87b5d4', fontFamily: 'monospace' }}>{Math.round(vol * 100)}</div>
        </div>
        <button onClick={() => setTrackMutes(prev => ({ ...prev, [drumKey]: !prev[drumKey] }))} style={{ background: muted ? '#5a5a5a' : '#8daa9d', border: 'none', borderRadius: 4, padding: 6, cursor: 'pointer', boxShadow: '0 2px 4px rgba(0,0,0,0.5)', minWidth: 32, minHeight: 28, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{muted ? <VolumeX size={14} color="#f5ebe0" /> : <Volume2 size={14} color="#0a0a0a" />}</button>
      </div>
    );
  };

  return (
    <>
      <style>{`
        @keyframes pulse { 0%, 100% { box-shadow: 0 0 25px rgba(212,107,90,1); background: #d46b5a; } 50% { box-shadow: 0 0 45px rgba(255,120,90,1); background: #e07560; } }
        @keyframes pulseLight { 0%, 100% { box-shadow: 0 0 20px rgba(255,48,48,1); background: #ff3030; } 50% { box-shadow: 0 0 35px rgba(255,70,70,1); background: #ff5050; } }
        @keyframes tubeGlow { 0%, 100% { text-shadow: 0 0 8px rgba(212,107,90,0.6), 0 0 12px rgba(212,107,90,0.4); } 50% { text-shadow: 0 0 12px rgba(212,107,90,0.8), 0 0 18px rgba(212,107,90,0.6), 0 0 24px rgba(212,107,90,0.4); } }
        @keyframes eqGlow { 0%, 100% { box-shadow: 0 0 10px currentColor, 0 0 15px currentColor; } 50% { box-shadow: 0 0 15px currentColor, 0 0 25px currentColor, 0 0 35px currentColor; } }
      `}</style>
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 20px' }}>
      <div style={{ width: '100%', maxWidth: 1500, background: '#1a1a1a', backgroundImage: 'linear-gradient(145deg, #282828 0%, #1a1a1a 50%, #202020 100%)', padding: 40, borderRadius: 24, boxShadow: '0 60px 140px rgba(0,0,0,0.5)', border: '3px solid #0a0a0a', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 12, left: 12, right: 12, bottom: 12, borderRadius: 20, boxShadow: 'inset 0 0 30px rgba(0,0,0,0.95)', pointerEvents: 'none', border: '1px solid rgba(0,0,0,0.6)' }} />
        
        <div style={{ background: '#F0EEE6', borderRadius: 12, padding: 32, boxShadow: '0 28px 75px rgba(0,0,0,0.92)', border: '3px solid #D0CEC6', position: 'relative', zIndex: 1, margin: 0 }}>
          
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                <div style={{ fontSize: 52, fontWeight: 'bold', color: '#d46b5a', letterSpacing: 4, lineHeight: 1, fontFamily: 'Georgia, serif' }}>CPC</div>
                <div style={{ fontSize: 47, color: '#2d2d2d', lineHeight: 1, fontWeight: 500, fontStyle: 'italic', fontFamily: 'monospace' }}>3000</div>
                {isRecordingMaster && <div style={{ width: 12, height: 12, background: '#ff3030', borderRadius: '50%', marginLeft: 16, boxShadow: '0 0 20px rgba(255,48,48,1)', animation: 'pulseLight 1s ease-in-out infinite' }} />}
              </div>
              <div style={{ fontSize: 13, color: '#7a7a7a', fontFamily: 'monospace', marginTop: 4 }}>Powered by OTIS AMP®</div>
            </div>
            
            <div style={{ width: 550, height: 90, background: '#f5ebe0', borderRadius: 6, padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 20 }}>
              {Array(4).fill(0).map((_, col) => (
                <div key={col} style={{ display: 'flex', flexDirection: 'column', justifyContent: 'space-evenly', gap: 6 }}>
                  {Array(6).fill(0).map((_, row) => (
                    <div key={row} style={{ width: '100%', height: 5, background: '#0a0a0a', borderRadius: 3, boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.95)' }} />
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div style={{ background: 'linear-gradient(to bottom, #1a1a1a, #0a0a0a)', padding: 0, borderRadius: '8px 8px 0 0', boxShadow: 'inset 0 4px 20px rgba(0,0,0,0.95), 0 -6px 10px rgba(0,0,0,0.6), 3px -2px 8px rgba(0,0,0,0.3), -3px -2px 8px rgba(0,0,0,0.3)', marginBottom: 20, height: 300, border: '4px solid #0a0a0a', borderBottom: 'none', overflow: 'hidden', position: 'relative', transform: 'perspective(1200px) rotateX(-3deg)', transformOrigin: 'top center' }}>
            <div style={{ position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', fontSize: 18, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace', letterSpacing: 3, textShadow: '0 0 8px rgba(212,107,90,0.6)', animation: 'tubeGlow 3s ease-in-out infinite', zIndex: 10 }}>OTIS AMP®</div>
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 0, height: '100%', paddingTop: 36 }}>
              
              <div style={{ background: '#0a0a0a', borderRight: '2px solid #2a2a2a', display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ background: '#151515', padding: '6px 12px', borderBottom: '2px solid #0a0a0a' }}>
                  <span style={{ fontSize: 12, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace', letterSpacing: 2, textShadow: '0 0 8px rgba(212,107,90,0.6)', animation: 'tubeGlow 3s ease-in-out infinite' }}>TRACKS</span>
                </div>
                <div style={{ flex: 1, overflow: 'auto' }}>
                  {[{name: 'thinking', label: 'THINKING', time: '2:40'}, {name: 'creating', label: 'CREATING', time: '2:32'}, {name: 'coding', label: 'CODING', time: '2:56'}].map((track, idx) => (
                    <div key={track.name} onClick={() => { loadInspoTrack(track.name); setSelectedTrackId(null); }} style={{ padding: '8px 12px', background: activePreset === track.name ? '#d46b5a' : 'transparent', borderBottom: '2px solid #1a1a1a', cursor: 'pointer', color: activePreset === track.name ? '#0a0a0a' : '#8daa9d', display: 'flex', gap: 12, fontWeight: activePreset === track.name ? 'bold' : 'normal', fontFamily: 'monospace', fontSize: 12 }}>
                      <span style={{ minWidth: 24 }}>0{idx+1}</span>
                      <span style={{ flex: 1 }}>{track.label}</span>
                      <span style={{ opacity: 0.7 }}>{track.time}</span>
                    </div>
                  ))}
                  {recordedTracks.map((track, idx) => (
                    <div key={track.id} style={{ padding: '8px 12px', background: selectedTrackId === track.id ? '#87b5d4' : 'transparent', borderBottom: '2px solid #1a1a1a', color: selectedTrackId === track.id ? '#0a0a0a' : '#87b5d4', display: 'flex', gap: 8, alignItems: 'center', fontFamily: 'monospace', fontSize: 12, fontWeight: selectedTrackId === track.id ? 'bold' : 'normal' }}>
                      <span style={{ minWidth: 24 }}>0{idx + 4}</span>
                      {editingRecordingId === track.id ? (
                        <input type="text" value={editingRecordingName} onChange={(e) => setEditingRecordingName(e.target.value)} onBlur={() => {
                          if (editingRecordingName.trim()) {
                            setRecordedTracks(prev => prev.map(t => t.id === track.id ? {...t, name: editingRecordingName} : t));
                          }
                          setEditingRecordingId(null);
                        }} onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            if (editingRecordingName.trim()) {
                              setRecordedTracks(prev => prev.map(t => t.id === track.id ? {...t, name: editingRecordingName} : t));
                            }
                            setEditingRecordingId(null);
                          }
                        }} autoFocus style={{ flex: 1, fontSize: 12, color: '#87b5d4', fontFamily: 'monospace', background: '#1a1a1a', border: '1px solid #87b5d4', borderRadius: 2, padding: 2 }} />
                      ) : (
                        <span style={{ flex: 1, cursor: 'pointer' }} onDoubleClick={() => { setEditingRecordingId(track.id); setEditingRecordingName(track.name); }} onClick={() => {
                        setActivePreset(null);
                        setSelectedTrackId(track.id);
                        const hasData = (track.stepNotes && Object.keys(track.stepNotes).length > 0) || 
                          (track.drumTrack && Object.values(track.drumTrack).some(p => p.some(s => s === 1)));
                        if (hasData || track.bpm || track.instrument) {
                          setStepNotes(track.stepNotes || {});
                          setDrumTrack(track.drumTrack || drumTrack);
                          if (track.bpm) setBpm(track.bpm);
                          if (track.instrument) setSelectedInstrument(track.instrument);
                        } else if (track.recording) {
                          playRecordedTrack(track.recording);
                        } else {
                          setStepNotes({});
                          setDrumTrack({ kick: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], snare: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], hat: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], crash: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] });
                        }
                      }} title="Double-click to edit name">{track.name}</span>
                      )}
                      {track.recording && (
                        <button onClick={(e) => {
                          e.stopPropagation();
                          const nextKey = allKeys.find(k => !recordingKeyMap[k.index]);
                          if (nextKey) {
                            setRecordingKeyMap(prev => {
                              const existingKey = Object.keys(prev).find(k => prev[k] === track.id);
                              if (existingKey) {
                                const updated = {...prev};
                                delete updated[existingKey];
                                return updated;
                              } else {
                                setSelectedInstrument('recordings');
                                return { ...prev, [nextKey.index]: track.id };
                              }
                            });
                          }
                        }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: Object.keys(recordingKeyMap).find(k => recordingKeyMap[k] === track.id) ? '#d46b5a' : '#87b5d4', padding: 0, fontSize: 12, fontWeight: 'bold' }} title={Object.keys(recordingKeyMap).find(k => recordingKeyMap[k] === track.id) ? "Unmap" : "Map to key"}>{Object.keys(recordingKeyMap).find(k => recordingKeyMap[k] === track.id) ? '✓' : '+'}</button>
                      )}
                      <button onClick={() => setRecordedTracks(prev => prev.map(t => t.id === track.id ? { ...t, stepNotes: {...stepNotes}, drumTrack: {...drumTrack}, bpm, instrument: selectedInstrument } : t))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: track.stepNotes ? (selectedTrackId === track.id ? '#0a0a0a' : '#87b5d4') : '#5a5a5a', padding: 0, fontSize: 12 }}>↓</button>
                      <button onClick={() => { setRecordedTracks(prev => prev.filter(t => t.id !== track.id)); if (selectedTrackId === track.id) setSelectedTrackId(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: selectedTrackId === track.id ? '#c45b4a' : '#d46b5a', padding: 2 }}><Trash2 size={11} /></button>
                    </div>
                  ))}
                  <div onClick={() => {
                    setStepNotes({});
                    setDrumTrack({ kick: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], snare: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], hat: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], crash: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] });
                    setActivePreset(null);
                    const newTrackId = Date.now();
                    setSelectedTrackId(newTrackId);
                    setRecordedTracks(prev => [...prev, { id: newTrackId, name: 'Track ' + (recordedTracks.length + 4) }]);
                  }} style={{ padding: '8px 12px', background: 'transparent', borderBottom: '2px solid #1a1a1a', cursor: 'pointer', color: '#8daa9d', display: 'flex', gap: 12, fontFamily: 'monospace', fontSize: 12, fontWeight: 'bold' }}>
                    <span style={{ minWidth: 24 }}>+</span>
                    <span style={{ flex: 1 }}>NEW TRACK</span>
                  </div>
                </div>
                <div style={{ padding: '8px 12px', fontSize: 10, color: '#8daa9d', display: 'flex', justifyContent: 'space-between', fontFamily: 'monospace', borderTop: '2px solid #1a1a1a', background: '#0a0a0a' }}>
                  <span>PATTERN</span>
                  <span>{isRecordingMaster ? formatTime(elapsedTime) : '0:00'}</span>
                </div>
              </div>
              
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                <div style={{ flex: 1, background: '#0a0a0a', borderBottom: '2px solid #2a2a2a', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ background: '#151515', padding: '6px 12px', borderBottom: '2px solid #0a0a0a' }}>
                    <span style={{ fontSize: 12, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace', letterSpacing: 2, textShadow: '0 0 8px rgba(212,107,90,0.6)', animation: 'tubeGlow 3s ease-in-out infinite' }}>EQUALIZER</span>
                  </div>
                  <div style={{ flex: 1, padding: 12, display: 'flex', gap: 5, alignItems: 'flex-end', background: '#0a0a0a' }}>
                    {eqLevels.map((height, i) => (
                      <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', gap: 2 }}>
                        {[...Array(12)].map((_, j) => (
                          <div key={j} style={{ width: '100%', height: 6, background: j < Math.floor(height / 8) ? (j > 9 ? '#d46b5a' : j > 6 ? '#d4a297' : '#8daa9d') : '#0a1a0a', boxShadow: j < Math.floor(height / 8) ? '0 0 10px currentColor, 0 0 15px currentColor' : 'none', animation: j < Math.floor(height / 8) ? 'eqGlow 2s ease-in-out infinite' : 'none', borderRadius: 2 }} />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
                
                <div style={{ flex: 1, background: '#0a0a0a', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ background: '#151515', padding: '6px 12px', borderBottom: '2px solid #0a0a0a', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ fontSize: 12, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace', letterSpacing: 2, textShadow: '0 0 8px rgba(212,107,90,0.6)', animation: 'tubeGlow 3s ease-in-out infinite' }}>METRONOME</span>
                    <button onClick={() => setMetronomeEnabled(!metronomeEnabled)} style={{ background: metronomeEnabled ? '#d46b5a' : '#2a2a2a', border: '2px solid ' + (metronomeEnabled ? '#c45b4a' : '#1a1a1a'), borderRadius: 4, padding: '4px 8px', fontSize: 9, fontWeight: 'bold', color: metronomeEnabled ? '#0a0a0a' : '#8daa9d', cursor: 'pointer', fontFamily: 'monospace' }}>{metronomeEnabled ? 'ON' : 'OFF'}</button>
                  </div>
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a0a0a', position: 'relative', padding: 20 }}>
                    <div style={{ position: 'relative', width: '100%', height: 70 }}>
                      {[25, 50, 75].map(pos => <div key={pos} style={{ position: 'absolute', left: pos + '%', top: '20%', width: 1, height: '60%', background: '#2a2a2a' }} />)}
                      <div style={{ position: 'absolute', left: '50%', top: '10%', width: 2, height: '80%', background: '#3a3a3a' }} />
                      {isPlaying && <div style={{ position: 'absolute', left: metronomeNeedlePosition + '%', top: '50%', transform: 'translate(-50%, -50%)', width: 4, height: 50, background: '#d46b5a', borderRadius: 2, boxShadow: '0 0 10px #d46b5a', transition: 'left 0.1s ease-out' }} />}
                    </div>
                    <div style={{ position: 'absolute', bottom: 8, left: '50%', transform: 'translateX(-50%)', fontSize: 11, color: '#8daa9d', fontFamily: 'monospace', fontWeight: 'bold' }}>{bpm} BPM</div>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ background: '#0a0a0a', borderTop: '3px solid #1a1a1a', padding: '16px 20px', display: 'flex', gap: 20, alignItems: 'flex-end', height: 160 }}>
              {['nitroLow', 'nitroHigh', 'nitroRes', 'punchCutoff', 'punchRes', 'gridDelay', 'gridFeedback', 'drive'].map((param, idx) => {
                const groups = [
                  {label: [{main:'NITRO',sub:'LOW'},{main:'NITRO',sub:'HI'},{main:'NITRO',sub:'RES'}], color: '#8daa9d'}, 
                  {label: [{main:'PUNCH',sub:'CUT'},{main:'PUNCH',sub:'RES'}], color: '#d46b5a'}, 
                  {label: [{main:'DELAY',sub:'TIME'},{main:'DELAY',sub:'FDBK'}], color: '#87b5d4'}, 
                  {label: [{main:'DRIVE',sub:'AMT'}], color: '#c28e7f'}
                ];
                const groupIdx = idx < 3 ? 0 : idx < 5 ? 1 : idx < 7 ? 2 : 3;
                const labelIdx = idx < 3 ? idx : idx < 5 ? idx - 3 : idx < 7 ? idx - 5 : 0;
                const color = groups[groupIdx].color;
                const labels = groups[groupIdx].label[labelIdx];
                return (
                  <React.Fragment key={param}>
                    {groupIdx > 0 && labelIdx === 0 && <div style={{ width: 2, height: 90, background: '#2a2a2a', alignSelf: 'center' }} />}
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                      <div style={{ position: 'relative', width: 14, height: 85, background: '#1a1a1a', borderRadius: 7, border: '3px solid #0a0a0a', marginTop: 8, boxShadow: 'inset 0 3px 8px rgba(0,0,0,0.9)' }}>
                        <div style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: Math.min(effects[param], 100) + '%', background: 'linear-gradient(to top, ' + color + ', ' + color + 'aa)', borderRadius: 5, transition: 'height 0.15s' }} />
                        <div style={{ position: 'absolute', bottom: Math.min(effects[param], 100) + '%', left: '50%', transform: 'translate(-50%, 50%)', width: 20, height: 20, background: 'linear-gradient(135deg, #d0d0d0, #b0b0b0, #a0a0a0)', border: '3px solid #707070', borderRadius: '50%', boxShadow: '0 3px 6px rgba(0,0,0,0.7), inset 0 2px 0 rgba(255,255,255,0.5)', cursor: 'grab' }} />
                        <input type="range" min="0" max="100" value={effects[param]} onChange={(e) => setEffects(p => ({...p, [param]: parseInt(e.target.value)}))} orient="vertical" style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%) rotate(-90deg)', width: 85, height: 14, opacity: 0, cursor: 'pointer' }} />
                      </div>
                      <span style={{ fontSize: 11, color: '#d4a297', fontFamily: 'monospace', fontWeight: 'bold' }}>{effects[param]}</span>
                      <span style={{ fontSize: 8, color, fontFamily: 'monospace', fontWeight: 'bold' }}>{labels.main}</span>
                      <span style={{ fontSize: 7, color: color + 'cc', fontFamily: 'monospace' }}>{labels.sub}</span>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'stretch' }}>
            <button onClick={() => {
              if (isRecordingMaster) {
                setIsRecordingMaster(false);
                if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
                  mediaRecorderRef.current.stop();
                }
                setRecordingType(null);
                setElapsedTime(0);
              } else {
                startMasterRecording();
              }
            }} style={{ padding: '16px 28px', background: isRecordingMaster ? '#d46b5a' : '#e07b6a', border: '4px ridge ' + (isRecordingMaster ? '#c45b4a' : '#d06b5a'), borderRadius: 10, fontSize: 14, fontWeight: 'bold', color: '#f5ebe0', cursor: 'pointer', boxShadow: isRecordingMaster ? '0 0 30px rgba(212,107,90,1)' : '0 5px 10px rgba(0,0,0,0.7)', animation: isRecordingMaster ? 'pulse 1s ease-in-out infinite' : 'none', letterSpacing: 1, minWidth: 170, textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>
                {isRecordingMaster && <span>●</span>}
                <span>{isRecordingMaster ? 'STOP' : 'REC ALL'}</span>
                {isRecordingMaster && <span style={{ minWidth: 40, fontFamily: 'monospace' }}>{formatTime(elapsedTime)}</span>}
              </div>
            </button>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '8px 16px', background: '#5a5a5a', borderRadius: 8, border: '3px ridge #4a4a4a', justifyContent: 'center' }}>
              <div style={{ fontSize: 9, color: '#c4bbb0', fontWeight: 'bold', fontFamily: 'monospace', textAlign: 'center', letterSpacing: 1 }}>MASTER</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <Volume2 size={15} color="#c4bbb0" />
                <div style={{ position: 'relative', width: 110, height: 24, display: 'flex', alignItems: 'center' }}>
                  <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 3, background: '#2a2a2a', boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.9)', borderRadius: 2 }} />
                  <div style={{ position: 'absolute', left: (masterVolume * 100) + '%', top: '50%', transform: 'translate(-50%, -50%)', width: 28, height: 20, background: 'linear-gradient(135deg, #d0d0d0, #b0b0b0, #a0a0a0)', border: '3px solid #707070', borderRadius: 3, boxShadow: '0 4px 8px rgba(0,0,0,0.8), inset 0 2px 0 rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }}>
                    <div style={{ width: 2, height: 14, background: '#505050', borderRadius: 1 }} />
                  </div>
                  <input type="range" min="0" max="1" step="0.01" value={masterVolume} onChange={(e) => setMasterVolume(parseFloat(e.target.value))} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                </div>
                <div style={{ background: '#1a1a1a', border: '2px solid #0a0a0a', borderRadius: 3, padding: '3px 6px', minWidth: 32, textAlign: 'center' }}>
                  <div style={{ fontSize: 9, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace' }}>{Math.round(masterVolume * 100)}</div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', padding: '8px 14px', background: '#5a5a5a', borderRadius: 8, border: '3px ridge #4a4a4a' }}>
              <div style={{ fontSize: 9, color: '#c4bbb0', fontWeight: 'bold', fontFamily: 'monospace', letterSpacing: 1 }}>FX</div>
              {['reverb', 'distortion', 'delay'].map((effect) => (
                <div key={effect} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
                  <div style={{ fontSize: 7, color: '#c4bbb0', fontFamily: 'monospace', textTransform: 'uppercase', fontWeight: 'bold' }}>{effect.substring(0,3)}</div>
                  <button onClick={() => setActiveEffects(prev => ({...prev, [effect]: !prev[effect]}))} style={{ width: 32, height: 44, background: activeEffects[effect] ? 'linear-gradient(0deg, #2a2a2a 0%, #1a1a1a 50%, #0a0a0a 100%)' : 'linear-gradient(180deg, #2a2a2a 0%, #1a1a1a 50%, #0a0a0a 100%)', border: '3px ridge ' + (activeEffects[effect] ? '#3a3a3a' : '#1a1a1a'), borderRadius: 6, cursor: 'pointer', position: 'relative', boxShadow: activeEffects[effect] ? '0 4px 6px rgba(0,0,0,0.5)' : 'inset 0 4px 6px rgba(0,0,0,0.9), 0 2px 4px rgba(0,0,0,0.5)', transition: 'all 0.1s', transform: activeEffects[effect] ? 'translateY(0)' : 'translateY(3px)' }}>
                    <div style={{ position: 'absolute', top: activeEffects[effect] ? 6 : 20, left: '50%', transform: 'translateX(-50%)', width: 18, height: 18, background: activeEffects[effect] ? '#8daa9d' : '#5a5a5a', border: '2px solid ' + (activeEffects[effect] ? '#7d9a8d' : '#4a4a4a'), borderRadius: 3, boxShadow: activeEffects[effect] ? '0 2px 4px rgba(141,170,157,0.6)' : '0 2px 4px rgba(0,0,0,0.5)', transition: 'all 0.1s' }} />
                  </button>
                </div>
              ))}
            </div>
            
            <button onClick={() => { setStepNotes({}); setDrumTrack({ kick: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], snare: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], hat: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], tom: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], crash: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0], ride: [0,0,0,0,0,0,0,0,0,0,0,0,0,0,0,0] }); }} style={{ padding: '14px 20px', background: '#F0EEE6', border: '4px ridge #E0DED6', borderRadius: 10, fontSize: 12, fontWeight: 'bold', color: '#2d2d2d', cursor: 'pointer', letterSpacing: 1, boxShadow: '0 4px 8px rgba(0,0,0,0.3)', display: 'flex', alignItems: 'center' }}>CLEAR</button>
            
            <div style={{ flex: 1 }} />
            
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', background: '#5a5a5a', borderRadius: 8, border: '3px ridge #4a4a4a' }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <div style={{ fontSize: 8, color: '#c4bbb0', fontWeight: 'bold', fontFamily: 'monospace', textAlign: 'center', letterSpacing: 1 }}>TEMPO</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 9, color: '#c4bbb0', fontWeight: 'bold', fontFamily: 'monospace' }}>60</span>
                  <div style={{ position: 'relative', width: 100, height: 24 }}>
                    <div style={{ position: 'absolute', left: 0, right: 0, top: '50%', transform: 'translateY(-50%)', height: 3, background: '#2a2a2a', boxShadow: 'inset 0 2px 3px rgba(0,0,0,0.9)', borderRadius: 2 }} />
                    <div style={{ position: 'absolute', left: ((bpm - 60) / 120 * 100) + '%', top: '50%', transform: 'translate(-50%, -50%)', width: 28, height: 20, background: 'linear-gradient(135deg, #d0d0d0, #b0b0b0, #a0a0a0)', border: '3px solid #707070', borderRadius: 3, boxShadow: '0 4px 8px rgba(0,0,0,0.8), inset 0 2px 0 rgba(255,255,255,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'grab' }}>
                      <div style={{ width: 2, height: 14, background: '#505050', borderRadius: 1 }} />
                    </div>
                    <input type="range" min="60" max="180" value={bpm} onChange={(e) => setBpm(parseInt(e.target.value))} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }} />
                  </div>
                  <span style={{ fontSize: 9, color: '#c4bbb0', fontWeight: 'bold', fontFamily: 'monospace' }}>180</span>
                </div>
                <div style={{ background: '#1a1a1a', border: '2px solid #0a0a0a', borderRadius: 3, padding: '3px 6px', textAlign: 'center' }}>
                  <div style={{ fontSize: 9, fontWeight: 'bold', color: '#8daa9d', fontFamily: 'monospace' }}>{bpm}</div>
                </div>
              </div>
            </div>
            
            <button onClick={() => setIsPlaying(!isPlaying)} style={{ background: isPlaying ? '#d46b5a' : '#8daa9d', border: '4px ridge ' + (isPlaying ? '#c45b4a' : '#7d9a8d'), borderRadius: 10, padding: '14px 28px', fontSize: 15, fontWeight: 'bold', color: '#f5ebe0', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, letterSpacing: 1, minWidth: 100 }}>
              {isPlaying ? <Square size={24} fill="#f5ebe0" /> : <Play size={24} fill="#f5ebe0" />}
            </button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 12 }}>
            {['kick', 'snare', 'hat'].map(drum => (
              <div key={drum} style={{ background: '#1a1a1a', backgroundImage: 'linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 50%, #252525 100%)', padding: 12, borderRadius: 8, border: '3px ridge #4a4a4a' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 'bold', color: '#f5ebe0', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Arial, sans-serif' }}>{drum}</div>
                  <VolumeSlider drum={drum} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(16, 1fr)', gap: 3 }}>
                  {drumTrack[drum].map((active, step) => (
                    <button key={step} onClick={() => toggleDrumStep(drum, step)} style={{ aspectRatio: 1, background: active ? '#d46b5a' : '#1a1a1a', border: currentStep === step && isPlaying ? '2px solid #d4a297' : '1px solid #0a0a0a', borderRadius: 3, cursor: 'pointer', minWidth: 16, boxShadow: active ? '0 3px 6px rgba(212,107,90,0.6)' : 'inset 0 3px 5px rgba(0,0,0,0.8)' }} />
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
            {['tom', 'crash', 'ride'].map(drum => (
              <div key={drum} style={{ background: '#1a1a1a', backgroundImage: 'linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 50%, #252525 100%)', padding: 12, borderRadius: 8, border: '3px ridge #4a4a4a' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                  <select value={drumInstruments[drum]} onChange={(e) => setDrumInstruments(prev => ({...prev, [drum]: e.target.value}))} style={{ fontSize: 12, fontWeight: 'bold', color: '#f5ebe0', textTransform: 'uppercase', letterSpacing: 1, background: '#1a1a1a', border: '2px solid #2a2a2a', borderRadius: 4, padding: '4px 8px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
                    <option value="tom">TOM</option>
                    <option value="clap">CLAP</option>
                    <option value="cowbell">COWBELL</option>
                    <option value="shaker">SHAKER</option>
                    <option value="crash">CRASH</option>
                    <option value="ride">RIDE</option>
                  </select>
                  <VolumeSlider drum={drum} isCustom={true} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(16, 1fr)', gap: 3 }}>
                  {drumTrack[drum].map((active, step) => (
                    <button key={step} onClick={() => toggleDrumStep(drum, step)} style={{ aspectRatio: 1, background: active ? '#d46b5a' : '#1a1a1a', border: currentStep === step && isPlaying ? '2px solid #d4a297' : '1px solid #0a0a0a', borderRadius: 3, cursor: 'pointer', minWidth: 16, boxShadow: active ? '0 3px 6px rgba(212,107,90,0.6)' : 'inset 0 3px 5px rgba(0,0,0,0.8)' }} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div style={{ background: '#1a1a1a', backgroundImage: 'linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 50%, #252525 100%)', padding: 12, borderRadius: 8, border: '3px ridge #4a4a4a', marginBottom: 16 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontSize: 12, fontWeight: 'bold', color: '#f5ebe0', textTransform: 'uppercase', letterSpacing: 1, fontFamily: 'Arial, sans-serif' }}>SEQUENCER</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <VolumeSlider drum="piano" />
                <button onClick={() => { setStepNotes({}); setSelectedStep(null); }} style={{ background: '#5a5a5a', border: '2px solid #4a4a4a', borderRadius: 4, padding: '5px 10px', fontSize: 10, fontWeight: 'bold', color: '#f5ebe0', cursor: 'pointer', fontFamily: 'monospace' }}>CLEAR</button>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(16, 1fr)', gap: 3 }}>
              {Array(16).fill(0).map((_, step) => {
                const hasNotes = stepNotes[step]?.length > 0;
                const isSelected = selectedStep === step;
                return (
                  <button key={step} onClick={() => setSelectedStep(selectedStep === step ? null : step)} style={{ aspectRatio: 1, background: hasNotes ? '#87b5d4' : '#1a1a1a', border: isSelected ? '3px solid #d4a297' : currentStep === step && isPlaying ? '2px solid #d4a297' : '1px solid #0a0a0a', borderRadius: 3, cursor: 'pointer', minWidth: 16, fontSize: hasNotes ? 9 : 7, fontWeight: 'bold', color: hasNotes ? '#0a0a0a' : '#5a5a5a', fontFamily: 'monospace' }}>{hasNotes ? '♪' + stepNotes[step].length : step + 1}</button>
                );
              })}
            </div>
            {selectedStep !== null && (
              <div style={{ marginTop: 10, background: '#0a0a0a', border: '2px solid #87b5d4', borderRadius: 6, padding: 10 }}>
                <div style={{ fontSize: 9, fontWeight: 'bold', color: '#87b5d4', fontFamily: 'monospace', marginBottom: 8, textAlign: 'center' }}>STEP {selectedStep + 1} - {selectedInstrument === 'recordings' ? 'PRESS MAPPED KEYS' : 'CLICK KEYS TO BUILD'}</div>
                <button onClick={() => setSelectedStep(null)} style={{ marginTop: 6, width: '100%', padding: 5, background: '#5a5a5a', border: '2px solid #4a4a4a', borderRadius: 4, fontSize: 9, fontWeight: 'bold', color: '#f5ebe0', cursor: 'pointer', fontFamily: 'monospace' }}>DONE</button>
              </div>
            )}
          </div>

          <div style={{ background: '#2d2d2d', border: '4px ridge #3a3a3a', borderRadius: 12, padding: 18, minHeight: 200 }}>
            <div style={{ display: 'flex', gap: 14, height: 200 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 100 }}>
                <div style={{ background: '#1a1a1a', border: '3px ridge #4a4a4a', borderRadius: 8, padding: 6 }}>
                  <div style={{ fontSize: 7, fontWeight: 'bold', color: '#f5ebe0', fontFamily: 'monospace', marginBottom: 4, textAlign: 'center' }}>MODE</div>
                  <select value={selectedInstrument} onChange={(e) => setSelectedInstrument(e.target.value)} style={{ width: '100%', padding: 4, background: '#1a1a1a', border: '2px solid #2a2a2a', borderRadius: 4, fontSize: 8, fontWeight: 'bold', cursor: 'pointer', color: '#d46b5a', fontFamily: 'monospace', textAlign: 'center' }}>
                    <option value="piano">PIANO</option>
                    <option value="bass">BASS</option>
                    <option value="synth">SYNTH</option>
                    <option value="drums">DRUMS</option>
                    <option value="recordings">RECORDINGS</option>
                  </select>
                </div>
                
                {selectedInstrument === 'recordings' && (
                  <div style={{ background: '#1a1a1a', border: '3px ridge #4a4a4a', borderRadius: 8, padding: 6, flex: 1, display: 'flex', flexDirection: 'column', gap: 5, overflow: 'hidden' }}>
                    <div style={{ fontSize: 7, fontWeight: 'bold', color: '#f5ebe0', fontFamily: 'monospace', textAlign: 'center' }}>RECORDINGS ({recordedTracks.filter(t => t.recording).length})</div>
                    
                    <div style={{ display: 'flex', gap: 2, height: 22, alignItems: 'flex-end', background: '#0a0a0a', borderRadius: 3, padding: 3, border: '2px solid #1a1a1a' }}>
                      {Array(12).fill(0).map((_, i) => (
                        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column-reverse', gap: 1 }}>
                          {Array(5).fill(0).map((_, j) => {
                            const barActive = micLevel > (i / 12) * 100 && micLevel > (j / 5) * 100;
                            return <div key={j} style={{ width: '100%', height: 3, background: barActive ? (j > 3 ? '#d46b5a' : j > 1 ? '#d4a297' : '#8daa9d') : '#1a1a1a', borderRadius: 1 }} />;
                          })}
                        </div>
                      ))}
                    </div>
                    
                    <div style={{ fontSize: 6, color: '#8daa9d', fontFamily: 'monospace', textAlign: 'center' }}>{isRecordingSample ? 'RECORDING' : 'READY'}</div>
                    <button onClick={() => {
                      if (isRecordingSample) {
                        stopSampleRecording();
                      } else {
                        setRecordingType('microphone');
                        startSampleRecording();
                      }
                    }} style={{ width: '100%', padding: 6, background: isRecordingSample ? '#d46b5a' : '#8daa9d', border: '2px solid ' + (isRecordingSample ? '#c45b4a' : '#7d9a8d'), borderRadius: 4, fontSize: 8, fontWeight: 'bold', cursor: 'pointer', color: '#f5ebe0', fontFamily: 'monospace', animation: isRecordingSample ? 'pulse 1s infinite' : 'none' }}>{isRecordingSample ? '●' : 'REC MIC'}</button>
                    
                    <div style={{ height: 1, background: '#2a2a2a', margin: '4px 0' }} />
                    
                    {recordedTracks.filter(t => t.recording).length === 0 ? (
                      <div style={{ fontSize: 6, color: '#8daa9d', fontFamily: 'monospace', textAlign: 'center', padding: 8, lineHeight: 1.4 }}>No recordings yet</div>
                    ) : (
                      <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 3, maxHeight: 80 }}>
                        {recordedTracks.filter(t => t.recording).map((rec) => {
                          const trackNum = recordedTracks.filter(t => t.recording).findIndex(t => t.id === rec.id) + 1;
                          const assignedKey = Object.keys(recordingKeyMap).find(k => recordingKeyMap[k] === rec.id);
                          const keyInfo = assignedKey !== undefined ? allKeys.find(k => k.index === parseInt(assignedKey)) : null;
                          const keyLabel = keyInfo ? keyInfo.note : null;
                          const duration = rec.duration ? formatTime(rec.duration) : '0:00';
                          return (
                            <div key={rec.id} style={{ background: '#0a0a0a', border: '1px solid #2a2a2a', borderRadius: 3, padding: '4px 6px', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span style={{ fontSize: 7, fontWeight: 'bold', color: '#d46b5a', fontFamily: 'monospace', minWidth: 14 }}>#{trackNum}</span>
                              {editingRecordingId === rec.id ? (
                                <input type="text" value={editingRecordingName} onChange={(e) => setEditingRecordingName(e.target.value)} onBlur={() => {
                                  if (editingRecordingName.trim()) {
                                    setRecordedTracks(prev => prev.map(t => t.id === rec.id ? {...t, name: editingRecordingName} : t));
                                  }
                                  setEditingRecordingId(null);
                                }} onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    if (editingRecordingName.trim()) {
                                      setRecordedTracks(prev => prev.map(t => t.id === rec.id ? {...t, name: editingRecordingName} : t));
                                    }
                                    setEditingRecordingId(null);
                                  }
                                }} autoFocus style={{ fontSize: 6, color: '#87b5d4', fontFamily: 'monospace', flex: 1, background: '#1a1a1a', border: '1px solid #87b5d4', borderRadius: 2, padding: 2 }} />
                              ) : (
                                <span onDoubleClick={() => { setEditingRecordingId(rec.id); setEditingRecordingName(rec.name); }} style={{ fontSize: 6, color: '#87b5d4', fontFamily: 'monospace', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', cursor: 'text' }} title="Double-click to edit">{rec.name}</span>
                              )}
                              <span style={{ fontSize: 5, color: '#8daa9d', fontFamily: 'monospace', opacity: 0.7 }}>{duration}</span>
                              {keyLabel && <span style={{ fontSize: 5, color: '#8daa9d', fontFamily: 'monospace', fontWeight: 'bold', background: '#1a1a1a', padding: '2px 4px', borderRadius: 2 }}>{keyLabel}</span>}
                              <button onClick={(e) => {
                                e.stopPropagation();
                                console.log('Playing recording:', rec.name, rec.recording);
                                if (rec.recording && rec.recording.length > 0) {
                                  playRecordedTrack(rec.recording);
                                }
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8daa9d', padding: 2, display: 'flex' }} title="Preview"><Play size={10} fill="#8daa9d" /></button>
                              <button onClick={(e) => {
                                e.stopPropagation();
                                const nextKey = allKeys.find(k => !recordingKeyMap[k.index]);
                                if (nextKey) {
                                  setRecordingKeyMap(prev => {
                                    const existingKey = Object.keys(prev).find(k => prev[k] === rec.id);
                                    if (existingKey) {
                                      const updated = {...prev};
                                      delete updated[existingKey];
                                      return updated;
                                    } else {
                                      return { ...prev, [nextKey.index]: rec.id };
                                    }
                                  });
                                }
                              }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: assignedKey !== undefined ? '#d46b5a' : '#87b5d4', padding: 2, fontSize: 12, fontWeight: 'bold' }} title={assignedKey !== undefined ? "Unassign" : "Map to key"}>{assignedKey !== undefined ? '✓' : '+'}</button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                    <div style={{ fontSize: 5, color: '#8daa9d', fontFamily: 'monospace', textAlign: 'center', padding: 4, background: '#0a0a0a', borderRadius: 2, lineHeight: 1.3 }}>
                      + maps • 2x click name
                    </div>
                  </div>
                )}
                
                {(selectedInstrument === 'piano' || selectedInstrument === 'bass' || selectedInstrument === 'synth') && (
                  <div style={{ background: '#1a1a1a', border: '3px ridge #4a4a4a', borderRadius: 8, padding: 6, flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <div style={{ fontSize: 7, fontWeight: 'bold', color: '#f5ebe0', fontFamily: 'monospace', textAlign: 'center' }}>OCT</div>
                    <button onClick={() => setOctave(Math.min(6, octave + 1))} style={{ width: '100%', height: 20, background: '#3a3a3a', color: '#f5ebe0', border: '2px solid #2a2a2a', borderRadius: 3, fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>▲</button>
                    <div style={{ fontSize: 28, fontWeight: 'bold', textAlign: 'center', background: '#1a1a1a', color: '#d46b5a', padding: '8px 0', borderRadius: 3, border: '2px solid #2a2a2a', fontFamily: 'monospace' }}>{octave}</div>
                    <button onClick={() => setOctave(Math.max(1, octave - 1))} style={{ width: '100%', height: 20, background: '#3a3a3a', color: '#f5ebe0', border: '2px solid #2a2a2a', borderRadius: 3, fontWeight: 'bold', fontSize: 12, cursor: 'pointer' }}>▼</button>
                  </div>
                )}
              </div>
              
              <div style={{ position: 'relative', flex: 1, background: '#0a0a0a', border: '4px solid #000', borderRadius: 10, overflow: 'hidden', padding: 8, boxShadow: 'inset 0 5px 15px rgba(0,0,0,0.98)' }}>
                <div style={{ display: 'flex', height: '100%', gap: 2 }}>
                  {(selectedInstrument === 'drums' ? drumPads : whiteKeys.map((n, idx) => ({note: n, label: n, drum: null, index: idx}))).map((item, i) => {
                    const isActive = pressedKey === item.note;
                    const noteId = item.note + '-' + item.index;
                    const isInStep = selectedStep !== null && stepNotes[selectedStep]?.some(n => (typeof n === 'string' ? n === item.note : n.id === noteId));
                    const hasMappedRec = selectedInstrument === 'recordings' && recordingKeyMap[i];
                    const mappedRec = hasMappedRec ? recordedTracks.find(t => t.id === recordingKeyMap[i]) : null;
                    const trackNum = mappedRec ? recordedTracks.filter(t => t.recording).findIndex(t => t.id === mappedRec.id) + 1 : null;
                    return (
                      <div key={i} style={{ flex: 1, position: 'relative' }}>
                        <button onMouseDown={() => playKey(item.note, item.drum, item.index)} onMouseUp={() => setPressedKey(null)} style={{ width: '100%', height: '100%', background: hasMappedRec ? 'linear-gradient(to bottom, #87b5d4, #77a5c4)' : isInStep ? 'linear-gradient(to bottom, #87b5d4, #77a5c4)' : isActive ? 'linear-gradient(to bottom, #d46b5a, #c25545)' : 'linear-gradient(to bottom, #fdfdfb, #f5f5f0)', border: hasMappedRec ? '2px solid #6795b4' : '2px solid #1a1a1a', borderRadius: '0 0 10px 10px', cursor: 'pointer', boxShadow: hasMappedRec ? '0 0 18px #87b5d4' : isInStep ? '0 0 15px #87b5d4' : isActive ? '0 0 15px #d46b5a' : 'inset 0 -3px 6px rgba(0,0,0,0.08)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
                          <div style={{ paddingBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            {hasMappedRec && <span style={{ fontSize: 10, fontWeight: 'bold', color: '#0a0a0a', fontFamily: 'monospace', background: 'rgba(0,0,0,0.2)', borderRadius: 2, padding: '2px 4px' }}>#{trackNum}</span>}
                            <span style={{ fontSize: selectedInstrument === 'drums' ? 7 : 11, fontWeight: 'bold', color: hasMappedRec ? '#0a0a0a' : isInStep || isActive ? '#f5ebe0' : '#2d2d2d', fontFamily: 'monospace', textShadow: hasMappedRec || isInStep || isActive ? '0 1px 2px rgba(0,0,0,0.3)' : '0 1px 1px rgba(255,255,255,0.8)' }}>{item.label}</span>
                          </div>
                        </button>
                      </div>
                    );
                  })}
                </div>
                {selectedInstrument !== 'drums' && (
                  <div style={{ position: 'absolute', top: 8, left: 8, right: 8, height: '55%', pointerEvents: 'none' }}>
                    {blackKeys.map(({pos, note}, blackIdx) => {
                      const isActive = pressedKey === note;
                      const keyIndex = 14 + blackIdx;
                      const noteId = note + '-' + keyIndex;
                      const isInStep = selectedStep !== null && stepNotes[selectedStep]?.some(n => (typeof n === 'string' ? n === note : n.id === noteId));
                      const hasMappedRec = selectedInstrument === 'recordings' && recordingKeyMap[keyIndex];
                      const mappedRec = hasMappedRec ? recordedTracks.find(t => t.id === recordingKeyMap[keyIndex]) : null;
                      const trackNum = mappedRec ? recordedTracks.filter(t => t.recording).findIndex(t => t.id === mappedRec.id) + 1 : null;
                      const whiteKeyWidth = 100 / 14;
                      const offset = whiteKeyWidth * pos + whiteKeyWidth * 0.7;
                      return (
                        <button key={pos} onMouseDown={() => playKey(note, null, keyIndex)} onMouseUp={() => setPressedKey(null)} style={{ position: 'absolute', left: offset + '%', width: (whiteKeyWidth * 0.6) + '%', height: '100%', background: hasMappedRec ? 'linear-gradient(to bottom, #5a7a8a, #4a6a7a)' : isInStep ? 'linear-gradient(to bottom, #5a5a5a, #7a7a7a)' : isActive ? 'linear-gradient(to bottom, #4a4a4a, #5a5a5a)' : 'linear-gradient(to bottom, #0a0a0a, #1a1a1a)', border: hasMappedRec ? '2px solid #4a6a7a' : '2px solid #000', borderRadius: '0 0 5px 5px', cursor: 'pointer', pointerEvents: 'auto', zIndex: 1, boxShadow: hasMappedRec ? '0 0 12px #5a7a8a' : 'none', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', padding: 0 }}>
                          <div style={{ paddingBottom: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                            {hasMappedRec && <span style={{ fontSize: 8, fontWeight: 'bold', color: '#87b5d4', fontFamily: 'monospace', background: 'rgba(0,0,0,0.4)', borderRadius: 2, padding: '1px 3px' }}>#{trackNum}</span>}
                            <span style={{ fontSize: 10, fontWeight: 'bold', color: hasMappedRec ? '#87b5d4' : isInStep ? '#87b5d4' : isActive ? '#d46b5a' : '#9dbaad', fontFamily: 'monospace', textShadow: '0 1px 3px rgba(0,0,0,0.9)' }}>{note}</span>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
    </>
  );
}

export default CPC3000;